/**
 * Shared Share Dropdown Component
 *
 * @deprecated This component is deprecated. Use separate ExportButton and ShareButton instead.
 * - ExportButton: Always-enabled export action (right-aligned in action bar)
 * - ShareButton: Selection-dependent share URLs action (left-aligned with other actions)
 *
 * Migration guide:
 * ```tsx
 * // OLD (deprecated):
 * <ShareDropdown
 *   onExportClick={() => setShowExportModal(true)}
 *   onShareClick={() => handlers.handleShare(assets)}
 * />
 *
 * // NEW:
 * <ShareButton
 *   onClick={() => handlers.handleShare(assets)}
 *   disabled={!hasSelection}
 * />
 * <ExportButton
 *   onClick={() => setShowExportModal(true)}
 * />
 * ```
 *
 * Provides consistent "Share" dropdown across sections with:
 * - Export (always present)
 * - Share URLs (optional, available in Assets and Technology)
 *
 * Sections control export/share logic through handler props.
 *
 * @example
 * ```tsx
 * // Basic usage with export only
 * <ShareDropdown
 *   onExportClick={handlers.handleExport}
 * />
 *
 * // With share and custom button label
 * <ShareDropdown
 *   onExportClick={() => setShowExportModal(true)}
 *   onShareClick={() => handlers.handleShare(assets)}
 *   labels={{ export: 'Export Assets' }}
 *   label="Share"
 * />
 * ```
 *
 * @module components/actions
 */

import { EllipsisHorizontalCircleIcon } from '@heroicons/react/24/solid';
import { useMemo } from 'react';

import { ACTION_ICONS } from '@/components/actions/constants/actionIcons';
import type { ShareDropdownProps } from '@/components/actions/types';
import { Dropdown } from '@/components/Dropdown';
import type { DropdownOption } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';

/**
 * Default labels for share actions
 */
const DEFAULT_LABELS = {
  export: 'Export Selected',
  share: 'Share URLs',
} as const;

/**
 * Share Dropdown Component
 *
 * @deprecated Use ExportButton and ShareButton instead. See module documentation for migration guide.
 *
 * Renders a dropdown with Export and optionally Share actions.
 * Share action only appears if onShareClick handler is provided.
 *
 * @param props - Component props
 * @returns Dropdown component
 */
export function ShareDropdown({
  onExportClick,
  onShareClick,
  labels,
  label = 'Share',
  disabled = false,
  className,
  'data-testid': testId = 'share-dropdown',
}: ShareDropdownProps) {
  /**
   * Build dropdown options based on configuration
   *
   * Memoized to prevent unnecessary re-renders.
   * Share action is conditionally included based on onShareClick.
   */
  const options = useMemo<DropdownOption[]>(() => {
    const items: DropdownOption[] = [];

    // Export action (always present)
    items.push({
      label: labels?.export ?? DEFAULT_LABELS.export,
      icon: <ACTION_ICONS.EXPORT className="size-4" />,
      onClick: onExportClick,
      disabled: disabled,
    });

    // Share action (optional - only if handler provided)
    if (onShareClick) {
      items.push({
        label: labels?.share ?? DEFAULT_LABELS.share,
        icon: <ACTION_ICONS.SHARE className="size-4" />,
        onClick: onShareClick,
        disabled: disabled,
      });
    }

    return items;
  }, [labels, disabled, onExportClick, onShareClick]);

  return (
    <div data-testid={testId}>
      <Dropdown
        title="Share Options"
        size="sm"
        options={options}
        placeholder={label}
        styleType="rounded"
        border={false}
        disabled={disabled}
        className={cn('[&_span:nth-child(3)]:hidden', className)}
        listClassName="!z-[150]"
        icon={<EllipsisHorizontalCircleIcon className="size-4" />}
      />
    </div>
  );
}
